import { createRef, useEffect, useState } from 'react';

import { FormControl, FormControlLabel, Radio, styled } from '@mui/material';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RECORDING_PATCH } from '../../config/apiPaths';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { processApiPath } from '../../utils/url';
import { ClassRecordingFormFields, Permission, RecordingDialogProps } from '../classroom/types';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import ControlledInput from '../forms/ControlledInput';
import ControlledRadioGroup from '../forms/ControlledRadioGroup';

const StyledForm = styled('form')`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const defaultValues: ClassRecordingFormFields = {
    name: '',
    permission: 'ADMINS'
};

export const EditRecordingDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, recording, recordings, setRecordings, onClose, classUuid, userUuid } = props;
    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid;
    const [confirming, setConfirming] = useState(false);

    // Note - isValid must be deconstructed here or it will always be false:
    // https://github.com/react-hook-form/react-hook-form/issues/2755#issuecomment-962274624
    const {
        handleSubmit,
        control,
        formState: { isValid },
        reset
    } = useForm({ defaultValues, shouldUnregister: true });

    const formRef: any = createRef();

    useEffect(() => {
        const defaults: ClassRecordingFormFields = {
            name: recording?.name ?? defaultValues.name,
            permission: recording?.permission ?? defaultValues.permission
        };
        reset(defaults);
    }, [reset, recording]);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const onSubmit = async (data: any) => {
        if (!isValid) {
            return;
        }

        if (!recording?.recordingId || !schoolUuid || !classUuid || !userUuid) {
            console.error('Missing required data.');
            return;
        }

        // Update context to reflect changes.
        const updatedRecordings = recordings.map((r) => {
            if (r.recordingId === recording?.recordingId) {
                return { ...r, ...data };
            }
            return r;
        });
        setRecordings(updatedRecordings);

        setConfirming(true);

        const url = processApiPath(RECORDING_PATCH, { schoolUuid, classUuid, recordingId: recording.recordingId });
        try {
            await axios.patch(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    user_uuid: userUuid
                }
            });
        } catch (e) {
            console.error(e);
            // TODO show error here.
        }
        reset(data);
        setConfirming(false);
        handleClose();
    };

    const submitForm = () => {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    };

    const handleDialogConfirmed = () => {
        submitForm();
    };

    const permissionItems: Permission[] = ['PUBLIC', 'ROSTER', 'ADMINS'];

    return (
        <ConfirmationDialog
            id="edit-recording"
            open={open}
            onClose={handleClose}
            title={t('lti:recordings.edit-title', { name: recording?.name ?? '' })}
            confirmLabel={t('common:save')}
            onConfirm={handleDialogConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <StyledForm ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <FormControl fullWidth>
                    <ControlledInput type="text" name="name" control={control} label={t('class:name')} />
                </FormControl>
                <FormControl fullWidth>
                    <ControlledRadioGroup name="permission" label={t('lti:recordings.permission')} control={control}>
                        {permissionItems.map((permissionItem, c) => (
                            <FormControlLabel
                                key={permissionItem}
                                name={`permission[${c}]`}
                                value={permissionItem}
                                control={<Radio />}
                                label={t(`lti:recordings.permissions.${permissionItem.toLowerCase()}`)}
                            />
                        ))}
                    </ControlledRadioGroup>
                </FormControl>
            </StyledForm>
        </ConfirmationDialog>
    );
};
